
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface DeliverySectionProps {
  isDelivery: boolean;
  setIsDelivery: (value: boolean) => void;
  selectedZone: string;
  setSelectedZone: (value: string) => void;
  deliveryAddress: string;
  setDeliveryAddress: (value: string) => void;
  customerName: string;
  setCustomerName: (value: string) => void;
  customerPhone: string;
  setCustomerPhone: (value: string) => void;
  deliveryPaymentType: string;
  setDeliveryPaymentType: (value: string) => void;
  customDeliveryAmount: string;
  setCustomDeliveryAmount: (value: string) => void;
  selectedDriver: string;
  setSelectedDriver: (value: string) => void;
  customDriverName: string;
  setCustomDriverName: (value: string) => void;
}

export const DeliverySection = ({
  isDelivery,
  setIsDelivery,
  selectedZone,
  setSelectedZone,
  deliveryAddress,
  setDeliveryAddress,
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  deliveryPaymentType,
  setDeliveryPaymentType,
  customDeliveryAmount,
  setCustomDeliveryAmount,
  selectedDriver,
  setSelectedDriver,
  customDriverName,
  setCustomDriverName,
}: DeliverySectionProps) => {
  const { data: zones } = useQuery({
    queryKey: ["delivery-zones"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("delivery_zones")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  const { data: drivers } = useQuery({
    queryKey: ["delivery-drivers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("delivery_drivers")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  const selectedZoneData = zones?.find(zone => zone.id === selectedZone);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <input
          type="checkbox"
          id="delivery-option"
          checked={isDelivery}
          onChange={(e) => setIsDelivery(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
          disabled
        />
        <Label htmlFor="delivery-option" className="flex items-center space-x-2">
          <span>Livraison à domicile</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>La livraison à domicile est en cours de développement. Cette fonctionnalité sera bientôt disponible.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
      </div>

      <div className={cn("space-y-4", !isDelivery && "hidden")}>
        <div className="space-y-2">
          <Label>Nom du client</Label>
          <Input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Nom complet du client"
          />
        </div>

        <div className="space-y-2">
          <Label>Numéro de téléphone</Label>
          <Input
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="Ex: 698123456"
            type="tel"
          />
        </div>

        <div className="space-y-2">
          <Label>Type de paiement de la livraison</Label>
          <RadioGroup value={deliveryPaymentType} onValueChange={setDeliveryPaymentType}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="direct" id="direct" />
              <Label htmlFor="direct">Paiement direct au livreur</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-gray-500 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Le client paiera le montant de la livraison directement au livreur lors de la réception</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="included" id="included" />
              <Label htmlFor="included">Inclus dans le paiement</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-gray-500 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Le client paie la livraison à l'entreprise qui ensuite rémunère le livreur</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </RadioGroup>
        </div>

        {deliveryPaymentType === 'direct' ? (
          <div className="space-y-2">
            <Label>Montant de la livraison</Label>
            <Input
              value={customDeliveryAmount}
              onChange={(e) => setCustomDeliveryAmount(e.target.value)}
              placeholder="Montant de la livraison"
              type="number"
            />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label>Zone de livraison</Label>
              <Select value={selectedZone} onValueChange={setSelectedZone}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une zone" />
                </SelectTrigger>
                <SelectContent>
                  {zones?.map((zone) => (
                    <SelectItem key={zone.id} value={zone.id}>
                      {zone.name} - {zone.price.toLocaleString()} FCFA
                      {zone.free_delivery_threshold && 
                        ` (Gratuit à partir de ${zone.free_delivery_threshold.toLocaleString()} FCFA)`}
                      {zone.estimated_time && ` - ${zone.estimated_time}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedZoneData?.minimum_order_amount && (
                <p className="text-sm text-muted-foreground mt-1">
                  Commande minimum : {selectedZoneData.minimum_order_amount.toLocaleString()} FCFA
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Sélectionner un livreur</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un livreur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Autre livreur</SelectItem>
                  {drivers?.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name} ({driver.driver_type === 'company' ? 'Entreprise' : 'Freelance'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedDriver === 'custom' && (
              <div className="space-y-2">
                <Label>Nom du livreur</Label>
                <Input
                  value={customDriverName}
                  onChange={(e) => setCustomDriverName(e.target.value)}
                  placeholder="Nom du livreur"
                />
              </div>
            )}
          </>
        )}

        <div className="space-y-2">
          <Label>Adresse de livraison</Label>
          <Input
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
            placeholder="Adresse complète de livraison"
          />
        </div>
      </div>
    </div>
  );
};
